
import React from 'react';
import { Target, Lock, TrendingUp, Clock, Heart, Coins, GraduationCap, Building2, DollarSign, LineChart, Shield, Globe, Handshake, Users, Rocket } from 'lucide-react';
import { Separator } from "@/components/ui/separator";
import { AspectRatio } from "@/components/ui/aspect-ratio";

const AcquisitionStrategySection = () => {
  return <div className="p-8 flex flex-col h-full">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#0D1F2D] mb-4">
          Why VMS Is Our Acquisition Focus
        </h2>
        <p className="text-base text-gray-600 max-w-3xl mb-6">VMS companies provide mission-critical, industry-specific platforms that integrate deeply into customer workflows—becoming the indispensable backbone of their operations.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <Lock className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Low Churn & High Switching Costs</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Predictable Recurring Cash Flows</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <Target className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Up-Sell & Pricing Power</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <LineChart className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Efficient Unit Economics</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <Shield className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Low Technology Risk</span>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="p-1 rounded-lg bg-blue-50">
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm text-gray-700">Scalable Roll-Up Synergies</span>
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#0D1F2D] mb-4">How Serial Acquisition VMS HoldCo create value</h2>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#F1F1F1] p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-medium text-sm mt-1">
                1
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0D1F2D] mb-1">Acquire High-Quality VMS
              </h3>
                <p className="text-sm text-gray-600 leading-tight">Generating durable, high-visibility recurring revenues and cash flows.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#F1F1F1] p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-medium text-sm mt-1">
                2
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0D1F2D] mb-1">Acquisition Arbitrage
              </h3>
                <p className="text-sm text-gray-600 leading-tight">Small-cap VMS targets trade at discounted multiples due to limited buyer competition.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#F1F1F1] p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-medium text-sm mt-1">
                3
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0D1F2D] mb-1">Multiple Expansion
              </h3>
                <p className="text-sm text-gray-600 leading-tight">Portfolio companies earn premium valuations under a unified, de-risked HoldCo umbrella.</p>
              </div>
            </div>
          </div>

          <div className="bg-[#F1F1F1] p-4 rounded-lg">
            <div className="flex items-start gap-2">
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white font-medium text-sm mt-1">
                4
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#0D1F2D] mb-1">Scalable Cash-Flow Base</h3>
                <p className="text-sm text-gray-600 leading-tight">Combining cash flows from the portfolio builds a larger capital base to deploy in new acquisitions.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator className="my-4" />

      <div>
        <h2 className="text-2xl font-bold text-[#0D1F2D] mb-4">Delivering Superior Long-Term Returns </h2>
        <div className="mt-2 text-base text-gray-600 mb-4">
          <p>By building a self-funding growth engine where compounding cash flows become the primary source of capital for new acquisitions—minimizing external funding and maximizing returns for existing shareholders.
        </p>
        </div>
          
        <div className="w-full max-w-xl mx-auto">
          <AspectRatio ratio={16 / 9} className="bg-white">
            <img src="/lovable-uploads/b6acdb83-c375-4e71-9bae-866ba28b4893.png" alt="Cumulative Stock Price Appreciation (%) of Selected Global VMS Serial Acquirers | January 15-25, 2025" className="w-full h-full object-contain" />
          </AspectRatio>
        </div>
      </div>
    </div>;
};

export default AcquisitionStrategySection;
